// Google-Sheets-style "Connected Sheet" toolbar that sits above the
// data table. Mirrors the toolbar Google Sheets renders for BigQuery-
// connected sheets:
//   Chart · Pivot table · Function · Extract · Calculated column ·
//   Column stats · Advanced analytics
//
// Each button is a thin wrapper around an existing Datahub
// affordance (or a stub for the ones we haven't built yet). The
// parent owns the actual handlers — this component is just the
// uniform Google-Sheets-styled row.

import * as React from 'react';

export type ToolbarAction =
  | 'chart'
  | 'pivot'
  | 'function'
  | 'extract'
  | 'calc_column'
  | 'column_stats'
  | 'advanced';

type Item = {
  id: ToolbarAction;
  label: string;
  icon: string;
  title: string;
  /** When false the button still shows but renders as disabled +
   *  surfaces 'Coming soon' on hover. Lets us keep the visual parity
   *  with Google Sheets without shipping every feature on day one. */
  enabled: boolean;
};

const ITEMS: Item[] = [
  { id: 'chart',        label: 'Chart',              icon: '📊', title: 'Visualise rows as a chart (line / bar / pie). Coming soon — pivot + column stats already cover most operator needs.', enabled: false },
  { id: 'pivot',        label: 'Pivot table',        icon: '⌗',  title: 'Group rows by a column and render count + aggregates. Same as the Pivot button in the recipe rail.', enabled: true },
  { id: 'function',     label: 'Function',           icon: 'ƒ',  title: 'Apply a SQL function to the result set. Coming soon — use Calculated column for now.', enabled: false },
  { id: 'extract',      label: 'Extract',            icon: '📥', title: 'Save the current slice as a BigQuery view. The view tab appears in the row above and the SQL is available via the ⋯ menu.', enabled: true },
  { id: 'calc_column',  label: 'Calculated column',  icon: '➕', title: 'Add a derived column built from a SQL expression. Coming soon — the recipe editor already supports CAST picks.', enabled: false },
  { id: 'column_stats', label: 'Column stats',       icon: '📈', title: 'Open the distribution drawer for a column (top-50 values + counts + click-to-filter).', enabled: true },
  { id: 'advanced',     label: 'Advanced analytics', icon: '🔬', title: 'Time-series decomposition, anomaly detection, correlations. Coming soon.', enabled: false },
];

export function ConnectedSheetToolbar({
  onAction,
  fullscreen,
  onToggleFullscreen,
}: {
  onAction: (id: ToolbarAction) => void;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
}): React.ReactElement {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '6px 10px',
      background: 'var(--bg-elev)',
      border: '1px solid var(--line)',
      borderRadius: 6,
      flexWrap: 'wrap',
    }}>
      {ITEMS.map(item => (
        <button
          key={item.id}
          type="button"
          onClick={item.enabled ? () => onAction(item.id) : undefined}
          disabled={!item.enabled}
          title={item.title}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 10px',
            background: 'transparent',
            border: '1px solid transparent',
            borderRadius: 4,
            color: item.enabled ? 'var(--ink-2)' : 'var(--ink-3)',
            fontSize: 12.5, fontFamily: 'var(--ui)',
            cursor: item.enabled ? 'pointer' : 'not-allowed',
            opacity: item.enabled ? 1 : 0.55,
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => {
            if (item.enabled) e.currentTarget.style.background = 'var(--bg-sunken)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <span style={{ fontSize: 13, lineHeight: 1 }}>{item.icon}</span>
          <span>{item.label}</span>
        </button>
      ))}
      <span style={{ flex: 1 }} />
      <button
        type="button"
        onClick={onToggleFullscreen}
        title={fullscreen ? 'Exit full-screen (Esc)' : 'Full-screen the data table (like Google Sheets)'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 10px',
          background: fullscreen ? 'var(--accent-soft)' : 'transparent',
          border: `1px solid ${fullscreen ? 'var(--accent-line)' : 'transparent'}`,
          borderRadius: 4,
          color: fullscreen ? 'var(--accent)' : 'var(--ink-2)',
          fontSize: 12.5, fontFamily: 'var(--ui)',
          cursor: 'pointer',
        }}
      >
        <span style={{ fontSize: 13, lineHeight: 1 }}>{fullscreen ? '⛶' : '⛶'}</span>
        <span>{fullscreen ? 'Exit full-screen' : 'Full-screen'}</span>
      </button>
    </div>
  );
}
